import { AcademicCapIcon, BriefcaseIcon, CalendarDaysIcon, CheckCircleIcon, ExclamationTriangleIcon, NoSymbolIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { TrainingClassStatus, type UserTrainingClass, getTrainingDueDate } from 'pollworker/src/state/training';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { buttonVariants } from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';
import { phoneNumberFormatter } from 'shared/src/utils/formatters';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

function MetaItem({ icon: Icon, children, className }: { icon: HeroIcon; children: ReactNode; className?: string }) {
  return (
    <li className={cn('flex items-center gap-2', className)}>
      <Icon className="size-5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </li>
  );
}

function statusIcon(status: TrainingClassStatus, completed?: boolean, cancelled?: boolean): HeroIcon {
  if (completed || status === TrainingClassStatus.Completed) return CheckCircleIcon;
  if (cancelled || status === TrainingClassStatus.Cancelled) return NoSymbolIcon;
  if (status === TrainingClassStatus.Scheduled) return CalendarDaysIcon;
  return ExclamationTriangleIcon;
}

function statusClassName(status: TrainingClassStatus, completed?: boolean, cancelled?: boolean) {
  if (completed || status === TrainingClassStatus.Completed) return 'text-green-600';
  if (cancelled || status === TrainingClassStatus.Cancelled) return 'text-gray-500';
  if (status === TrainingClassStatus.Unavailable) return 'text-amber-700';
  return 'text-gray-700';
}

export default function TrainingClassMeta({
  trainingClass,
  statusMessage,
  completed,
  cancelled,
}: {
  trainingClass: UserTrainingClass;
  statusMessage?: string | null;
  completed?: boolean;
  cancelled?: boolean;
}) {
  const dueDate = completed || cancelled || trainingClass.Status === TrainingClassStatus.Scheduled ? null : getTrainingDueDate(trainingClass);
  const formattedPhone = trainingClass.Phone ? phoneNumberFormatter({ value: trainingClass.Phone }) : undefined;

  return (
    <ul className="mt-2 space-y-1 text-sm text-gray-600">
      {!trainingClass.IsExtraTraining && trainingClass.RequiredForRoles.length > 0 ? <MetaItem icon={BriefcaseIcon}>{trainingClass.RequiredForRoles.join(', ')}</MetaItem> : null}
      {trainingClass.IsExtraTraining ? <MetaItem icon={AcademicCapIcon}>Self-enrolled training</MetaItem> : null}
      {dueDate ? (
        <MetaItem icon={CalendarDaysIcon} className={dueDate.isPastDue ? 'font-medium text-red-600' : undefined}>
          {dueDate.text}
        </MetaItem>
      ) : null}
      {trainingClass.ContactName ? (
        <MetaItem icon={UserCircleIcon}>
          {trainingClass.ContactName}
          {trainingClass.Phone ? (
            <span>
              {' · '}
              <a className={buttonVariants({ variant: 'link', size: 'invisible' })} href={`tel:${trainingClass.Phone}`}>
                {formattedPhone}
              </a>
            </span>
          ) : null}
        </MetaItem>
      ) : null}
      {statusMessage ? (
        <MetaItem icon={statusIcon(trainingClass.Status, completed, cancelled)} className={statusClassName(trainingClass.Status, completed, cancelled)}>
          {statusMessage}
        </MetaItem>
      ) : null}
    </ul>
  );
}
